import type {
  ApiResponse,
  WorldModelSummary,
  Goal,
  Insight,
  Extraction,
  RecallResult,
} from './types';

// Pillar URLs - production
const PILLARS = {
  billmem: 'https://billmem.bill-burkey.workers.dev',
  worldmodel: 'https://worldmodel.bill-burkey.workers.dev',
  watchdog: 'https://watchdog.bill-burkey.workers.dev',
  synthesizer: 'https://synthesizer.bill-burkey.workers.dev',
  reporadar: 'https://reporadar.bill-burkey.workers.dev',
} as const;

async function fetchApi<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || `HTTP ${response.status}` };
    }

    return data as ApiResponse<T>;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

// WorldModel API
export async function getWorldModelSummary(): Promise<ApiResponse<WorldModelSummary>> {
  return fetchApi<WorldModelSummary>(`${PILLARS.worldmodel}/api/summary`);
}

export async function getGoals(): Promise<ApiResponse<{ goals: Goal[] }>> {
  return fetchApi<{ goals: Goal[] }>(`${PILLARS.worldmodel}/api/goals`);
}

export async function updateGoalProgress(
  goalId: string,
  progress: number
): Promise<ApiResponse<{ goal: Goal }>> {
  return fetchApi<{ goal: Goal }>(`${PILLARS.worldmodel}/api/goals/${goalId}`, {
    method: 'PATCH',
    body: JSON.stringify({ progress }),
  });
}

// Watchdog API
export async function getInsights(
  acknowledged?: boolean
): Promise<ApiResponse<{ insights: Insight[] }>> {
  const params = acknowledged !== undefined ? `?acknowledged=${acknowledged}` : '';
  return fetchApi<{ insights: Insight[] }>(`${PILLARS.watchdog}/api/insights${params}`);
}

export async function acknowledgeInsight(
  insightId: string
): Promise<ApiResponse<{ insight: Insight }>> {
  return fetchApi<{ insight: Insight }>(
    `${PILLARS.watchdog}/api/insights/${insightId}/acknowledge`,
    { method: 'POST' }
  );
}

// Synthesizer API
export async function extractContent(
  content: string,
  sourceType: string = 'dashboard'
): Promise<ApiResponse<{ extraction: Extraction }>> {
  return fetchApi<{ extraction: Extraction }>(`${PILLARS.synthesizer}/api/extract`, {
    method: 'POST',
    body: JSON.stringify({ content, source_type: sourceType }),
  });
}

export async function getExtractions(
  limit: number = 10
): Promise<ApiResponse<{ extractions: Extraction[] }>> {
  return fetchApi<{ extractions: Extraction[] }>(
    `${PILLARS.synthesizer}/api/extractions?limit=${limit}`
  );
}

// BillMem API
export async function recallMemories(
  query: string,
  limit: number = 10
): Promise<ApiResponse<RecallResult>> {
  return fetchApi<RecallResult>(
    `${PILLARS.billmem}/api/recall?q=${encodeURIComponent(query)}&limit=${limit}`
  );
}

export async function ingestMemory(
  content: string,
  source: string
): Promise<ApiResponse<{ memory_id: string }>> {
  return fetchApi<{ memory_id: string }>(`${PILLARS.billmem}/api/ingest`, {
    method: 'POST',
    body: JSON.stringify({ content, source }),
  });
}

// Health checks
export async function checkPillarHealth(): Promise<Record<string, boolean>> {
  const results: Record<string, boolean> = {};

  await Promise.all(
    Object.entries(PILLARS).map(async ([name, url]) => {
      try {
        const response = await fetch(`${url}/health`, { method: 'GET' });
        results[name] = response.ok;
      } catch {
        results[name] = false;
      }
    })
  );

  return results;
}
